import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, Car, Menu, X } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import QualificationForm from "../components/QualificationForm";

const LOGO_URL = "/logo.png";
const HERO_IMAGE = "https://customer-assets-0z36b82j.emergentagent.net/job_404047a2-5b3a-4a5c-970f-2293d8b931fa/artifacts/klo3ym38_Web5.webp";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Trish Drives Honda" className="h-14 md:h-16 object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            <Link to="/" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Home</Link>
            <Link to="/cars" className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-2 rounded-lg font-medium transition-all duration-200">Cars</Link>
            <Link to="/consultation" className="text-white bg-[#E91E8C] px-4 py-2 rounded-lg font-medium">Book Consultation</Link>
          </nav>

          <div className="flex items-center gap-3 md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#E91E8C] text-white"
              data-testid="consultation-mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <nav className="flex flex-col p-4 space-y-2">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-3 rounded-lg font-medium transition-all">Home</Link>
            <Link to="/cars" onClick={() => setMobileMenuOpen(false)} className="text-[#1A1A1A] hover:text-white hover:bg-[#E91E8C] px-4 py-3 rounded-lg font-medium transition-all">Cars</Link>
            <Link to="/consultation" onClick={() => setMobileMenuOpen(false)} className="text-white bg-[#E91E8C] px-4 py-3 rounded-lg font-medium">Book Consultation</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

const ConsultationPage = () => {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles?show_on_cars=true`);
        setVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      }
    };
    fetchVehicles();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F9] via-white to-[#FFF5F9]">
      <Header />

      <main className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">

            {/* Left Side - Copy + Form */}
            <div className="order-2 lg:order-1" data-testid="consultation-form-section">
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 bg-[#E91E8C]/10 text-[#E91E8C] px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Phone className="w-4 h-4" />
                  Book a Consultation
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] leading-tight mb-4">
                  Your Dream Honda<br />
                  <span className="text-[#E91E8C]">Awaits You</span>
                </h1>
                <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed">
                  Answer a few quick questions and we'll tailor the perfect Honda experience just for you.
                </p>
              </div>

              <QualificationForm vehicles={vehicles} context="consultation" />

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 mt-6">
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <div className="w-8 h-8 rounded-full bg-[#E91E8C]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#E91E8C]" />
                  </div>
                  <span className="text-sm font-medium">Fast Response</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <div className="w-8 h-8 rounded-full bg-[#E91E8C]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#E91E8C]" />
                  </div>
                  <span className="text-sm font-medium">No Obligation</span>
                </div>
                <div className="flex items-center gap-2 text-[#4A4A4A]">
                  <div className="w-8 h-8 rounded-full bg-[#E91E8C]/10 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#E91E8C]" />
                  </div>
                  <span className="text-sm font-medium">Expert Advice</span>
                </div>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="order-1 lg:order-2 relative lg:sticky lg:top-24" data-testid="consultation-hero-image">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Trish with Honda"
                  className="w-full h-[300px] md:h-[400px] lg:h-[600px] object-cover object-[25%_center]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E91E8C] flex items-center justify-center flex-shrink-0">
                      <Car className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-[#1A1A1A]">New 2026 Models Available</p>
                      <p className="text-sm text-[#6B5E55]">Elevate, HR-V, CR-V & more</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img src={LOGO_URL} alt="Trish Drives Honda" className="h-12 mx-auto mb-4 brightness-0 invert" />
          <p className="text-white/60 text-sm">© 2026 Trish Drives Honda. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ConsultationPage;
